import { useRef, useState } from "react";
import { Paperclip, Camera, X, FileText, Image as ImageIcon, Loader2 } from "lucide-react";
import useAttachments from "@features/budget/hooks/useAttachments";

export default function Attachments({ budgetId }) {
  const { items, upload, remove } = useAttachments(budgetId);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      await upload(file);
    } catch (err) {
      console.error(err);
      alert("Upload failed: " + err.message);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const isImage = (contentType) => contentType?.startsWith("image/");

  const count = items.length;

  const handlePaperclipClick = () => {
    if (count > 0) {
      setOpen(!open);
    } else {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className="relative inline-flex items-center gap-1">
      <button
        onClick={handlePaperclipClick}
        disabled={busy}
        className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors disabled:opacity-50 ${
          count > 0
            ? "text-emerald-400 bg-emerald-500/15 hover:bg-emerald-500/25"
            : "text-gray-500 hover:text-gray-300"
        }`}
        title={count > 0 ? `${count} attachment${count > 1 ? "s" : ""}` : "Upload file"}
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
        {count > 0 && <span className="text-[10px] font-mono">{count}</span>}
      </button>
      <button
        onClick={() => cameraInputRef.current?.click()}
        disabled={busy}
        className="text-gray-500 hover:text-gray-300 transition-colors disabled:opacity-50"
        title="Take photo"
      >
        <Camera className="w-3 h-3" />
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={handleFile}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFile}
      />

      {/* Popover list */}
      {open && count > 0 && (
        <div
          className="absolute z-50 top-6 right-0 w-64 rounded-lg p-2 shadow-xl"
          style={{
            background: "var(--bg-card-frosted)",
            border: "1px solid var(--border-card-frosted)",
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
              Attachments
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
                className="text-[10px] text-gray-400 hover:text-gray-200 disabled:opacity-50"
                title="Upload another file"
              >
                + Add
              </button>
              <button
                onClick={() => setOpen(false)}
                className="text-gray-500 hover:text-gray-300"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
          <div className="space-y-1.5 max-h-60 overflow-y-auto">
            {items.map((att) => (
              <div
                key={att.id}
                className="flex items-center gap-2 p-1.5 rounded group"
                style={{ background: "var(--bg-row-hover)" }}
              >
                {isImage(att.contentType) ? (
                  <img
                    src={att.url}
                    alt={att.name}
                    className="w-8 h-8 rounded object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-8 h-8 rounded flex items-center justify-center bg-white/[0.04] flex-shrink-0">
                    <FileText className="w-4 h-4 text-gray-500" />
                  </div>
                )}
                <a
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-[11px] text-gray-300 hover:text-white truncate"
                  title={att.name}
                >
                  {att.name}
                </a>
                <button
                  onClick={() => remove(att)}
                  className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-400 transition-opacity flex-shrink-0"
                  title="Delete"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
